import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Check, Copy, Globe, Loader2, Send } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { APP_CONFIG } from '@/config/app';
import {
  type CompanyEmailSettings,
  type DnsRecord,
  useEmailDomainAction,
  useEmailSettings,
  useUpdateEmailSettings,
} from '@/hooks/useEmailSettings';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';

// APP_CONFIG.platformMailDomain mirrors PLATFORM_MAIL_DOMAIN on the edge
// functions; only used to preview the address before a send confirms it.
export function previewFromAddress(s: CompanyEmailSettings): string {
  if (s.custom_domain_status === 'verified' && s.custom_domain) return `${s.from_local_part}@${s.custom_domain}`;
  const slug = (s.platform_local_part ?? '').trim();
  const local = slug || s.public_key.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '') || 'mail';
  return `${local}@${APP_CONFIG.platformMailDomain}`;
}

function DnsRecordsTable({ records }: { records: DnsRecord[] }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };
  const cell = (value: string, key: string, max: string) => (
    <button type="button" className={cn('group inline-flex items-start gap-1 text-left font-mono', max)} onClick={() => copy(value, key)} title="Copy">
      <span className="break-all">{value}</span>
      {copied === key ? (
        <Check className="mt-0.5 h-3 w-3 shrink-0 text-success" />
      ) : (
        <Copy className="mt-0.5 h-3 w-3 shrink-0 opacity-0 group-hover:opacity-60" />
      )}
    </button>
  );
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 text-left text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Host / Name</th>
            <th className="px-3 py-2 font-medium">Value</th>
            <th className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={`${r.purpose}-${r.host}`} className="border-t align-top">
              <td className="px-3 py-2 font-mono">{r.type}</td>
              <td className="px-3 py-2">{cell(r.host, `h-${r.host}`, 'max-w-[240px]')}</td>
              <td className="px-3 py-2">{cell(r.value, `v-${r.host}`, 'max-w-[320px]')}</td>
              <td className="px-3 py-2">
                <span className={cn('font-medium', r.verified ? 'text-success' : 'text-muted-foreground')}>
                  {r.verified ? 'Verified' : 'Pending'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Settings body for the tenant's sending identity, custom domain and quota. */
export function EmailSendingPanel() {
  const { data: settings, isLoading, error } = useEmailSettings();
  const update = useUpdateEmailSettings();
  const domainAction = useEmailDomainAction();
  const { locale, timezone, formatShortDate } = useCompanyLocale();

  const [fromName, setFromName] = useState('');
  const [fromLocalPart, setFromLocalPart] = useState('hello');
  const [forwardTo, setForwardTo] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [busy, setBusy] = useState<'create' | 'verify' | 'remove' | 'test' | null>(null);

  useEffect(() => {
    if (!settings) return;
    setFromName(settings.from_name ?? '');
    setFromLocalPart(settings.from_local_part ?? 'hello');
    setForwardTo(settings.forward_inbound_to ?? '');
    setDomainInput(settings.custom_domain ?? '');
  }, [settings]);

  const runDomain = async (action: 'create' | 'verify' | 'remove' | 'test') => {
    setBusy(action);
    try {
      const res = await domainAction.mutateAsync({ action, domain: action === 'create' ? domainInput : undefined });
      if (action === 'test') {
        toast({ title: 'Test email sent', description: `From ${res.from} to ${res.to}. Check your inbox (and spam folder).` });
      } else if (action === 'create') {
        toast({ title: 'Domain added', description: 'Add the DNS records below, then click "Check verification".' });
      } else if (action === 'verify') {
        toast(
          res.status === 'verified'
            ? { title: 'Domain verified', description: 'Emails now send from your own domain.' }
            : { title: 'Not verified yet', description: 'DNS changes can take up to an hour to propagate. Try again shortly.' },
        );
      } else {
        toast({ title: 'Custom domain removed', description: 'Emails send from your platform address again.' });
      }
    } catch (e) {
      toast({
        title: action === 'test' ? 'Test email failed' : 'Domain update failed',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const saveIdentity = async () => {
    try {
      await update.mutateAsync({ from_name: fromName, from_local_part: fromLocalPart, forward_inbound_to: forwardTo });
      toast({ title: 'Email settings saved' });
    } catch (e) {
      toast({ title: 'Could not save', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading email settings...
      </div>
    );
  }
  if (error || !settings) {
    return <div className="text-sm text-destructive">{error instanceof Error ? error.message : 'Could not load email settings.'}</div>;
  }

  const dirty =
    fromName !== (settings.from_name ?? '') ||
    fromLocalPart !== settings.from_local_part ||
    forwardTo !== (settings.forward_inbound_to ?? '');
  const quotaPct = settings.monthly_quota > 0 ? Math.min(100, Math.round((settings.sent_this_month / settings.monthly_quota) * 100)) : 0;
  const fromAddress = previewFromAddress(settings);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        The address your quotes, invoices and messages are sent from. Customer replies land back on the lead automatically.
      </p>

      {settings.sending_paused && (
        <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <div className="font-medium">Email sending is paused</div>
            <div className="text-muted-foreground">{settings.paused_reason || 'Contact support to restore sending.'}</div>
          </div>
        </div>
      )}

      {/* Current identity + usage */}
      <div className="rounded-lg border p-3">
        <div className="text-xs text-muted-foreground">Emails are sent as</div>
        <div className="mt-0.5 text-sm font-medium">
          {settings.from_name || settings.company_name}{' '}
          <span className="font-mono text-muted-foreground">&lt;{fromAddress}&gt;</span>
        </div>
        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            {settings.sent_this_month.toLocaleString()} of {settings.monthly_quota.toLocaleString()} emails this month
          </span>
          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full', quotaPct >= 90 ? 'bg-destructive' : quotaPct >= 70 ? 'bg-warning' : 'bg-primary')}
              style={{ width: `${quotaPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Identity fields */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email-from-name">Sender name</Label>
          <Input id="email-from-name" placeholder={settings.company_name} value={fromName} onChange={(e) => setFromName(e.target.value)} maxLength={80} />
          <p className="text-xs text-muted-foreground">Shown as the sender in your customers' inbox. Defaults to your company name.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email-forward">Forward replies to</Label>
          <Input id="email-forward" type="email" placeholder="you@yourbusiness.com.au" value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} />
          <p className="text-xs text-muted-foreground">Optional. Replies always appear on the lead; this also sends you a copy.</p>
        </div>
        {settings.custom_domain_status === 'verified' && (
          <div className="space-y-2">
            <Label htmlFor="email-local">From address</Label>
            <div className="flex items-center gap-1">
              <Input id="email-local" className="max-w-[160px]" value={fromLocalPart} onChange={(e) => setFromLocalPart(e.target.value.toLowerCase())} maxLength={64} />
              <span className="font-mono text-sm text-muted-foreground">@{settings.custom_domain}</span>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={saveIdentity} disabled={!dirty || update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => runDomain('test')} disabled={busy !== null || settings.sending_paused}>
            <Send className="mr-1 h-4 w-4" />
            {busy === 'test' ? 'Sending…' : 'Send me a test email'}
          </Button>
        </div>
      </div>

      {/* Custom domain */}
      <div className="space-y-3 border-t pt-5">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Globe className="h-4 w-4" />
          Send from your own domain
          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </div>
        {settings.custom_domain_status === 'none' ? (
          <>
            <p className="text-sm text-muted-foreground">
              By default emails come from <span className="font-mono">{fromAddress}</span>, which works with no setup. To send from your own domain you'll need to add two DNS records — whoever manages your website can do this in a few minutes.
            </p>
            <div className="flex flex-wrap gap-2">
              <Input className="max-w-xs" placeholder="yourbusiness.com.au" value={domainInput} onChange={(e) => setDomainInput(e.target.value.trim())} />
              <Button size="sm" onClick={() => runDomain('create')} disabled={busy !== null || !domainInput}>
                {busy === 'create' ? 'Adding…' : 'Add domain'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono">{settings.custom_domain}</span>
              {settings.custom_domain_status === 'verified' ? (
                <span className="inline-flex items-center gap-1 text-xs text-success">
                  <Check className="h-3 w-3" /> Verified
                  {settings.domain_verified_at ? ` ${formatShortDate(settings.domain_verified_at)}` : ''}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Add these records at your DNS provider, then check.
                  {settings.domain_last_checked_at ? ` Last checked ${formatDocDate(settings.domain_last_checked_at, `${locale.shortDate} h:mm a`, timezone)}.` : ''}
                </span>
              )}
            </div>
            {settings.dns_records && settings.dns_records.length > 0 && <DnsRecordsTable records={settings.dns_records} />}
            <div className="flex flex-wrap gap-2">
              {settings.custom_domain_status !== 'verified' && (
                <Button size="sm" onClick={() => runDomain('verify')} disabled={busy !== null}>
                  {busy === 'verify' ? 'Checking…' : 'Check verification'}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => runDomain('remove')} disabled={busy !== null}>
                {busy === 'remove' ? 'Removing…' : 'Remove domain'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
